import { cn } from "../../../../libs";
import {
  VIEW_BASE,
  VIEW_COMPACT_SPACING,
  VIEW_HEADING_COMPACT,
  VIEW_HEADING_MINIMAL,
  VIEW_HEADING_NORMAL
} from "./tiptap.styles";

export type TiptapViewVariant = "normal" | "minimal" | "compact" | "plain";

export interface TiptapViewProps {
  content: string;
  variant?: TiptapViewVariant;
  className?: string;
}

const PARA_SENTINEL = "\x00";
const HEAD_SENTINEL = "\x01";
const PUNCT_RE = /[.!?:;…]$/;

function stripHtml(html: string): string {
  const raw = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/h[1-6]>/gi, HEAD_SENTINEL)
    .replace(/<\/p>/gi, PARA_SENTINEL)
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  const parts: Array<{ text: string; isHeading: boolean }> = [];
  let current = "";
  for (const ch of raw) {
    if (ch === PARA_SENTINEL || ch === HEAD_SENTINEL) {
      const trimmed = current.trim();
      if (trimmed) parts.push({ text: trimmed, isHeading: ch === HEAD_SENTINEL });
      current = "";
    } else {
      current += ch;
    }
  }
  const tail = current.trim();
  if (tail) parts.push({ text: tail, isHeading: false });

  return parts
    .map((part, i) => {
      if (i === parts.length - 1 || PUNCT_RE.test(part.text)) return part.text;
      return part.isHeading ? `${part.text}:` : `${part.text}.`;
    })
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

const VARIANT_CLASSES: Record<Exclude<TiptapViewVariant, "plain">, string> = {
  normal: cn(VIEW_BASE, VIEW_HEADING_NORMAL),
  minimal: cn(VIEW_BASE, VIEW_HEADING_MINIMAL),
  compact: cn(VIEW_BASE, VIEW_HEADING_COMPACT, VIEW_COMPACT_SPACING)
};

export const TiptapView = ({ content, variant = "normal", className }: TiptapViewProps) => {
  if (!content) return null;

  if (variant === "plain") {
    return (
      <p className={cn("text-text-primary text-responsive-base leading-relaxed", className)}>
        {stripHtml(content)}
      </p>
    );
  }

  return (
    <div className={cn(VARIANT_CLASSES[variant], className)} dangerouslySetInnerHTML={{ __html: content }} />
  );
};
